import { Text } from '@tremor/react';
import { localYMD } from '../lib/format';

interface Props {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}

// Preset specs run a closure against today's local clock, so refresh always
// resolves to the right window without us re-running the component when the
// calendar flips.
type Preset = { key: string; label: string; range: () => { from: string; to: string } };

function ymd(d: Date) { return localYMD(d); }

const PRESETS: Preset[] = [
  {
    key: 'today',  label: 'Hoy',
    range: () => { const t = new Date(); return { from: ymd(t), to: ymd(t) }; },
  },
  {
    key: 'yesterday', label: 'Ayer',
    range: () => {
      const t = new Date(); t.setDate(t.getDate() - 1);
      return { from: ymd(t), to: ymd(t) };
    },
  },
  {
    key: '7d', label: 'Últimos 7 días',
    range: () => {
      const t = new Date();
      const f = new Date(); f.setDate(f.getDate() - 6);
      return { from: ymd(f), to: ymd(t) };
    },
  },
  {
    key: '30d', label: 'Últimos 30 días',
    range: () => {
      const t = new Date();
      const f = new Date(); f.setDate(f.getDate() - 29);
      return { from: ymd(f), to: ymd(t) };
    },
  },
  {
    key: 'mtd', label: 'Este mes',
    range: () => {
      const t = new Date();
      return { from: ymd(new Date(t.getFullYear(), t.getMonth(), 1)), to: ymd(t) };
    },
  },
  {
    key: 'last_month', label: 'Mes pasado',
    range: () => {
      const t = new Date();
      const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const last  = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: ymd(first), to: ymd(last) };
    },
  },
];

/**
 * Date range editor with a preset bar above two labeled inputs. The active
 * preset is highlighted when the current (from, to) matches its range —
 * detected by literal string comparison since localYMD is canonical. Custom
 * dates leave every preset unhighlighted, which is the right signal that
 * the operator is in "manual" mode.
 *
 * Same value shape as the underlying bare inputs (`{from: "YYYY-MM-DD",
 * to: "YYYY-MM-DD"}`), so swapping a page over is a one-line change.
 */
export default function DateRangePresets({ from, to, onChange }: Props) {
  const activeKey = PRESETS.find((p) => {
    const r = p.range();
    return r.from === from && r.to === to;
  })?.key;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => {
          const active = p.key === activeKey;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onChange(p.range())}
              className={
                'text-xs px-2 py-1 rounded-tremor-small border transition-colors ' +
                (active
                  ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                  : 'bg-tremor-background text-tremor-content border-tremor-border hover:bg-tremor-background-muted')
              }
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <label className="text-sm">
          <div className="text-tremor-content text-xs mb-1">Desde</div>
          <input
            type="date"
            value={from}
            onChange={(e) => onChange({ from: e.target.value, to })}
            className="rounded border border-tremor-border px-2 py-1"
          />
        </label>
        <label className="text-sm">
          <div className="text-tremor-content text-xs mb-1">Hasta</div>
          <input
            type="date"
            value={to}
            onChange={(e) => onChange({ from, to: e.target.value })}
            className="rounded border border-tremor-border px-2 py-1"
          />
        </label>
        {activeKey === undefined && (
          <Text className="text-tremor-content text-xs italic self-center">Rango personalizado</Text>
        )}
      </div>
    </div>
  );
}
